import React, { FC, memo, useMemo } from 'react';
import { useIntl } from 'umi';
import BasicImage from '@home/components/BasicImage';
import { buildSkillDisplayViewModel } from '../../utils/display';
import { getSkillMdContent } from '../../utils/skillMd';
import { SKILL_DETAIL_ICONS } from '../../constants/skillDetail';
import SkillMarkdownViewer from '../../SkillDetail/components/SkillMarkdownViewer';
import type { ISkillData } from '../../types';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';
import './index.scss';

/**
 * Skill 详情轻量预览浮窗 Props（用于 SkillMention hover 展示）
 */
export interface ISkillDetailPreviewProps {
  /** 目标 Skill 数据 */
  skill: ISkillData;
  /** 是否英文环境 */
  isEN: boolean;
  /** 点击"查看详情"回调 */
  onViewDetail: (skillId: string) => void;
  /** 预览浮窗高度（与 Skill 列表下拉对齐时传入） */
  height?: number;
  /** 预览浮窗最小高度 */
  minHeight?: number;
  /** 是否隐藏内部 markdown 内容（SkillMention 下拉中使用） */
  hideMarkdown?: boolean;
}

const PREFIX = 'dba-skill-detail-preview';

/**
 * Skill 详情轻量预览浮窗（SkillMention hover）
 */
const SkillDetailPreview: FC<ISkillDetailPreviewProps> = ({
  skill,
  isEN,
  onViewDetail,
  height: _height,
  minHeight: _minHeight,
  hideMarkdown = false,
}) => {
  const { formatMessage } = useIntl();
  const display = buildSkillDisplayViewModel(skill, isEN);
  const md = useMemo(() => getSkillMdContent(skill, isEN), [skill, isEN]);

  return (
    <div
      className={`${PREFIX}${hideMarkdown ? ` ${PREFIX}--hide-markdown` : ''}`}
    >
      <div className={`${PREFIX}__header`}>
        <div className={`${PREFIX}__summary`}>
          <BasicImage
            src={skill.category_icon || SkillsDefaultIcon}
            alt={display.displayName}
            errorSrc={SKILL_DETAIL_ICONS.defaultSkill}
            className={`${PREFIX}__icon`}
          />
          <div className={`${PREFIX}__info`}>
            <div className={`${PREFIX}__title-row`}>
              <h4 className={`${PREFIX}__title`}>{display.displayName}</h4>
              <button
                type="button"
                className={`${PREFIX}__detail-link`}
                onClick={() => onViewDetail(skill.skill_id)}
              >
                {formatMessage({ id: 'dba_skills_mention_detail_link' })}
              </button>
            </div>

            <div className={`${PREFIX}__meta`}>
              <div className={`${PREFIX}__author`}>
                <BasicImage
                  src={display.authorAvatarSrc}
                  alt={display.authorDisplayName}
                  className={`${PREFIX}__avatar`}
                  errorSrc={SKILL_DETAIL_ICONS.authorDefault}
                />
                <span className={`${PREFIX}__author-name`}>
                  {display.authorDisplayName}
                </span>
              </div>
              <span className={`${PREFIX}__meta-divider`} />
              <span className={`${PREFIX}__meta-label`}>
                {formatMessage({ id: 'dba_skill_detail_usage_count' })}
              </span>
              <span className={`${PREFIX}__meta-value`}>
                {display.dialogCountText}
              </span>
              <span className={`${PREFIX}__meta-divider`} />
              <span className={`${PREFIX}__meta-label`}>
                {formatMessage({ id: 'dba_skill_detail_updated_at' })}
              </span>
              <span className={`${PREFIX}__meta-value`}>
                {display.updateTimeText}
              </span>
            </div>
          </div>
        </div>

        {display.description && (
          <p className={`${PREFIX}__description`}>{display.description}</p>
        )}
      </div>

      {!hideMarkdown && (
        <div className={`${PREFIX}__md`}>
          <SkillMarkdownViewer
            content={md?.content}
            loading={false}
            error={null}
          />
        </div>
      )}
    </div>
  );
};

export default memo(SkillDetailPreview);
